import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { isSupabaseConfigured, supabase } from "../supabase";
import { useAuth } from "../auth/useAuth";
import {
  listOverridesForMember,
  type CapabilityOverride,
} from "../permissions/overridesStore";
import { mergeCapabilities } from "../permissions/effective";
import type { Capability, Role } from "../permissions/capabilities";
import type {
  OrgMembership,
  OrgMemberStatus,
  OrgRole,
  Organization,
  ReviewMode,
} from "./types";

export interface OrgContextValue {
  memberships: OrgMembership[];
  activeOrg: Organization | null;
  setActiveOrg(id: string): void;
  loading: boolean;
  refresh(): Promise<void>;
  addBypassOrg(input: { name: string }): Promise<Organization>;
  setOrgName(id: string, name: string): Promise<void>;
  // Wave 11 — role resolution for the current user within the active org.
  // In bypass this honors proofly.bypass.activeRole.v1 so tests can swap roles
  // without reloading a different user. In real mode it comes from the
  // membership row.
  activeRole: OrgRole | null;
  // Wave 11 — bypass-only helper so tests (and dev tools) can flip the
  // active role programmatically. Writes the key and refreshes the in-memory
  // memberships without requiring a reload. Throws outside bypass mode.
  setBypassRole(role: OrgRole): void;
  // Wave 17 — update the review mode for an org (admin/owner only).
  setReviewMode(orgId: string, mode: ReviewMode): Promise<void>;
  // Wave 19 — the current user's per-member capability overrides for the
  // active org. Merged with role defaults by useCapabilities(). Empty
  // array means "no overrides, use role defaults verbatim".
  activeCapabilityOverrides: CapabilityOverride[];
  // Wave 19 — re-fetch the current user's overrides. Callers flip an
  // override (via setOverride) and then invoke this to refresh in-memory
  // state; no realtime subscription in this wave.
  refreshCapabilityOverrides(): Promise<void>;
}

export const OrgContext = createContext<OrgContextValue | undefined>(undefined);

const STORAGE_KEY = "proofly.activeOrg.v1";
const BYPASS_ORGS_KEY = "proofly.bypass.orgs.v1";
const BYPASS_ROLE_KEY = "proofly.bypass.activeRole.v1";

const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

let ACTIVE_ORG_ID: string | null = null;
const orgChangeListeners = new Set<() => void>();

export function getActiveOrganizationId(): string | null {
  return ACTIVE_ORG_ID;
}

export function subscribeActiveOrg(listener: () => void): () => void {
  orgChangeListeners.add(listener);
  return () => { orgChangeListeners.delete(listener); };
}

function setActiveOrgModule(next: string | null): void {
  if (next === ACTIVE_ORG_ID) return;
  ACTIVE_ORG_ID = next;
  for (const listener of orgChangeListeners) {
    try { listener(); } catch { /* swallow */ }
  }
}

const FAKE_ORG: Organization = {
  id: "org-e2e-00000000-0000-0000-0000-000000000000",
  name: "Personal",
  ownerId: "00000000-0000-0000-0000-000000000000",
  createdAt: new Date(0).toISOString(),
  updatedAt: new Date(0).toISOString(),
  reviewMode: "manual",
};

// Default bypass role. Wave 11 tests can flip this to 'employee' /
// 'admin' / etc via localStorage to drive the role-gated UI without a
// real Supabase membership. Left as 'owner' so every existing Wave
// 7a/7b test passes unchanged.
const BYPASS_DEFAULT_ROLE: OrgRole = "owner";

interface MembershipRow {
  role: OrgRole;
  status: OrgMemberStatus;
  joined_at: string | null;
  organizations: {
    id: string;
    name: string;
    owner_id: string;
    created_at: string;
    updated_at: string;
    review_mode: ReviewMode | null;
  } | null;
}

function readStoredActiveOrg(): string | null {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

function writeStoredActiveOrg(id: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (id == null) { window.localStorage.removeItem(STORAGE_KEY); }
    else { window.localStorage.setItem(STORAGE_KEY, id); }
  } catch { /* swallow */ }
}

function readBypassRole(): OrgRole {
  if (typeof window === "undefined") return BYPASS_DEFAULT_ROLE;
  try {
    const raw = window.localStorage.getItem(BYPASS_ROLE_KEY);
    if (!raw) return BYPASS_DEFAULT_ROLE;
    // The roadmap recipe suggests JSON-encoded string. Accept both plain and
    // JSON-encoded so tests can set either form.
    let parsed: unknown;
    try { parsed = JSON.parse(raw); }
    catch { parsed = raw; }
    if (typeof parsed === "string" && ["owner","admin","accountant","employee","auditor"].includes(parsed)) {
      return parsed as OrgRole;
    }
    return BYPASS_DEFAULT_ROLE;
  } catch { return BYPASS_DEFAULT_ROLE; }
}

interface StoredBypassOrg {
  id: string;
  name: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  reviewMode?: ReviewMode;
}

function readBypassOrgs(): StoredBypassOrg[] {
  if (typeof window === "undefined") return [FAKE_ORG];
  try {
    const raw = window.localStorage.getItem(BYPASS_ORGS_KEY);
    if (!raw) {
      const seed: StoredBypassOrg[] = [FAKE_ORG];
      window.localStorage.setItem(BYPASS_ORGS_KEY, JSON.stringify(seed));
      return seed;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) return [FAKE_ORG];
    return parsed as StoredBypassOrg[];
  } catch { return [FAKE_ORG]; }
}

function writeBypassOrgs(orgs: StoredBypassOrg[]): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(BYPASS_ORGS_KEY, JSON.stringify(orgs)); }
  catch { /* swallow */ }
}

function bypassMembershipsFromOrgs(orgs: StoredBypassOrg[]): OrgMembership[] {
  const role = readBypassRole();
  return orgs.map((o) => ({
    organization: {
      id: o.id,
      name: o.name,
      ownerId: o.ownerId,
      createdAt: o.createdAt,
      updatedAt: o.updatedAt,
      reviewMode: o.reviewMode ?? "manual",
    },
    role,
    status: "active",
    joinedAt: null,
  }));
}

interface OrgProviderProps { children: ReactNode; }

export function OrgProvider({ children }: OrgProviderProps) {
  const { user, loading: authLoading } = useAuth();

  const [memberships, setMemberships] = useState<OrgMembership[]>(() => {
    if (!E2E_BYPASS) return [];
    return bypassMembershipsFromOrgs(readBypassOrgs());
  });
  const [activeOrgId, setActiveOrgId] = useState<string | null>(() => {
    if (E2E_BYPASS) {
      const orgs = readBypassOrgs();
      const stored = readStoredActiveOrg();
      const validStored = stored && orgs.some((o) => o.id === stored) ? stored : null;
      const next = validStored ?? orgs[0]?.id ?? FAKE_ORG.id;
      setActiveOrgModule(next);
      return next;
    }
    const stored = readStoredActiveOrg();
    setActiveOrgModule(stored);
    return stored;
  });
  const [loading, setLoading] = useState<boolean>(!E2E_BYPASS);
  const fetchIdRef = useRef(0);
  const [activeCapabilityOverrides, setActiveCapabilityOverrides] = useState<
    CapabilityOverride[]
  >([]);
  const overridesFetchIdRef = useRef(0);

  useEffect(() => { setActiveOrgModule(activeOrgId); }, [activeOrgId]);

  // Watch the bypass-role storage key so tests that flip the role via
  // page.evaluate see the UI reflect it on reload (and across tabs).
  useEffect(() => {
    if (!E2E_BYPASS) return;
    const handler = (event: StorageEvent) => {
      if (event.key === BYPASS_ROLE_KEY) {
        setMemberships(bypassMembershipsFromOrgs(readBypassOrgs()));
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const loadMemberships = useCallback(async (): Promise<void> => {
    if (E2E_BYPASS) {
      const orgs = readBypassOrgs();
      setMemberships(bypassMembershipsFromOrgs(orgs));
      setActiveOrgId((prev) => {
        if (prev && orgs.some((o) => o.id === prev)) return prev;
        const stored = readStoredActiveOrg();
        const validStored = stored && orgs.some((o) => o.id === stored) ? stored : null;
        return validStored ?? orgs[0]?.id ?? FAKE_ORG.id;
      });
      setLoading(false);
      return;
    }

    if (!isSupabaseConfigured || !user) {
      setMemberships([]);
      setActiveOrgId(null);
      setLoading(false);
      return;
    }

    const fetchId = ++fetchIdRef.current;
    setLoading(true);

    const { data, error } = await supabase
      .from("org_members")
      .select("role, status, joined_at, organizations!inner(id, name, owner_id, created_at, updated_at, review_mode)")
      .eq("user_id", user.id)
      .eq("status", "active");

    if (fetchId !== fetchIdRef.current) return;

    if (error || !data) {
      // eslint-disable-next-line no-console
      console.warn(`[org] memberships load failed: ${error?.message ?? "unknown"}`);
      setMemberships([]);
      setActiveOrgId(null);
      setLoading(false);
      return;
    }

    const rows = data as unknown as MembershipRow[];
    const next: OrgMembership[] = rows
      .filter((r): r is MembershipRow & { organizations: NonNullable<MembershipRow["organizations"]>; } => r.organizations != null)
      .map((r) => ({
        organization: {
          id: r.organizations.id,
          name: r.organizations.name,
          ownerId: r.organizations.owner_id,
          createdAt: r.organizations.created_at,
          updatedAt: r.organizations.updated_at,
          reviewMode: r.organizations.review_mode ?? "manual",
        },
        role: r.role,
        status: r.status,
        joinedAt: r.joined_at,
      }));

    setMemberships(next);

    const stored = readStoredActiveOrg();
    const validStored = stored && next.some((m) => m.organization.id === stored) ? stored : null;
    const fallback = next[0]?.organization.id ?? null;
    const nextActive = validStored ?? fallback;

    setActiveOrgId(nextActive);
    if (nextActive !== stored) writeStoredActiveOrg(nextActive);

    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (E2E_BYPASS) return;
    if (authLoading) return;
    void loadMemberships();
  }, [authLoading, loadMemberships]);

  const setActiveOrg = useCallback((id: string) => {
    setActiveOrgId(id);
    writeStoredActiveOrg(id);
  }, []);

  const addBypassOrg = useCallback(async ({ name }: { name: string }): Promise<Organization> => {
    if (!E2E_BYPASS) { throw new Error("addBypassOrg is only available when VITE_E2E_BYPASS_AUTH=true"); }
    const now = new Date().toISOString();
    const newOrg: StoredBypassOrg = {
      id: `org-e2e-${crypto.randomUUID()}`,
      name: name.trim() || "Untitled",
      ownerId: FAKE_ORG.ownerId,
      createdAt: now,
      updatedAt: now,
      reviewMode: "manual",
    };
    const current = readBypassOrgs();
    const next = [...current, newOrg];
    writeBypassOrgs(next);
    setMemberships(bypassMembershipsFromOrgs(next));
    return {
      id: newOrg.id,
      name: newOrg.name,
      ownerId: newOrg.ownerId,
      createdAt: newOrg.createdAt,
      updatedAt: newOrg.updatedAt,
      reviewMode: newOrg.reviewMode ?? "manual",
    };
  }, []);

  const setBypassRole = useCallback((role: OrgRole): void => {
    if (!E2E_BYPASS) {
      throw new Error("setBypassRole is only available when VITE_E2E_BYPASS_AUTH=true");
    }
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(BYPASS_ROLE_KEY, JSON.stringify(role));
    } catch { /* swallow */ }
    // `storage` events don't fire in the same window that wrote the key, so
    // update our memberships directly AND dispatch a synthetic event so any
    // other OrgProvider instance on the page (tests, iframes) stays in sync.
    setMemberships(bypassMembershipsFromOrgs(readBypassOrgs()));
    try {
      window.dispatchEvent(new StorageEvent("storage", { key: BYPASS_ROLE_KEY }));
    } catch { /* swallow — older browsers don't allow the StorageEvent ctor */ }
  }, []);

  const setReviewMode = useCallback(async (orgId: string, mode: ReviewMode): Promise<void> => {
    if (E2E_BYPASS) {
      const current = readBypassOrgs();
      const next = current.map((o) =>
        o.id === orgId ? { ...o, reviewMode: mode, updatedAt: new Date().toISOString() } : o,
      );
      writeBypassOrgs(next);
      setMemberships(bypassMembershipsFromOrgs(next));
      return;
    }
    if (!isSupabaseConfigured) { throw new Error("Supabase is not configured"); }
    const { error } = await supabase
      .from("organizations")
      .update({ review_mode: mode })
      .eq("id", orgId);
    if (error) throw new Error(error.message);
    await loadMemberships();
  }, [loadMemberships]);

  // Wave 19 — load the current user's capability overrides for the active
  // org. Gracefully handles:
  //   - No user / no active org -> empty array.
  //   - Supabase error -> empty array (surfaced via console.warn; we don't
  //     want a permissions-matrix blip to strip caps from a live session).
  //   - Bypass mode -> reads from localStorage via the overrides store.
  const refreshCapabilityOverrides = useCallback(async (): Promise<void> => {
    const fetchId = ++overridesFetchIdRef.current;
    const userId = user?.id ?? null;
    if (!userId || !activeOrgId) {
      if (fetchId === overridesFetchIdRef.current) {
        setActiveCapabilityOverrides([]);
      }
      return;
    }
    try {
      const rows = await listOverridesForMember(activeOrgId, userId);
      if (fetchId !== overridesFetchIdRef.current) return;
      setActiveCapabilityOverrides(rows);
    } catch (err) {
      if (fetchId !== overridesFetchIdRef.current) return;
      // eslint-disable-next-line no-console
      console.warn(
        `[org] capability overrides load failed: ${err instanceof Error ? err.message : "unknown"}`,
      );
      setActiveCapabilityOverrides([]);
    }
  }, [user, activeOrgId]);

  useEffect(() => {
    void refreshCapabilityOverrides();
  }, [refreshCapabilityOverrides]);

  const setOrgName = useCallback(async (id: string, name: string): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error("Org name is required");
    if (E2E_BYPASS) {
      const current = readBypassOrgs();
      const next = current.map((o) => o.id === id ? { ...o, name: trimmed, updatedAt: new Date().toISOString() } : o);
      writeBypassOrgs(next);
      setMemberships(bypassMembershipsFromOrgs(next));
      return;
    }
    if (!isSupabaseConfigured) { throw new Error("Supabase is not configured"); }
    const { error } = await supabase.from("organizations").update({ name: trimmed }).eq("id", id);
    if (error) throw new Error(error.message);
    await loadMemberships();
  }, [loadMemberships]);

  // Wave 19 (E2E only): mirror the merged capability set on `window` so
  // the Playwright suite can assert against the hook's output without a
  // debug UI. The compiler tree-shakes this into a no-op outside bypass
  // mode because E2E_BYPASS is a build-time constant.
  useEffect(() => {
    if (!E2E_BYPASS) return;
    if (typeof window === "undefined") return;
    const match = memberships.find((m) => m.organization.id === activeOrgId);
    const role = (match?.role ?? null) as Role | null;
    const caps = mergeCapabilities(role, activeCapabilityOverrides);
    (window as unknown as {
      __proofly_test_caps__?: { role: Role | null; capabilities: Capability[] };
    }).__proofly_test_caps__ = {
      role,
      capabilities: Array.from(caps),
    };
  }, [memberships, activeOrgId, activeCapabilityOverrides]);

  const activeOrg = useMemo<Organization | null>(() => {
    const match = memberships.find((m) => m.organization.id === activeOrgId);
    return match?.organization ?? null;
  }, [memberships, activeOrgId]);

  const activeRole = useMemo<OrgRole | null>(() => {
    const match = memberships.find((m) => m.organization.id === activeOrgId);
    return match?.role ?? null;
  }, [memberships, activeOrgId]);

  const value = useMemo<OrgContextValue>(
    () => ({ memberships, activeOrg, setActiveOrg, loading, refresh: loadMemberships, addBypassOrg, setOrgName, activeRole, setBypassRole, setReviewMode, activeCapabilityOverrides, refreshCapabilityOverrides }),
    [memberships, activeOrg, setActiveOrg, loading, loadMemberships, addBypassOrg, setOrgName, activeRole, setBypassRole, setReviewMode, activeCapabilityOverrides, refreshCapabilityOverrides],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
